import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FILTER_OPERATORS, DEFAULT_OP, getOperator } from './filterModel';
import { FilterFunnelIcon } from './icons';

/**
 * Kolon başlığına gömülen filtre popover'ı (portal'lı).
 * filterType: 'text' | 'number' | 'date' | 'select' | 'asyncSelect'
 * Props:
 *   column        — TanStack column (header etiketi için)
 *   filter        — { op, value, value2 } | undefined
 *   filterType
 *   options       — select için { value, label }[]
 *   fetchOptions  — asyncSelect: (query) => Promise<{value,label}[]>
 *   resolveLabel  — asyncSelect: (value) => string (seçili kaydın etiketi)
 *   onApply(next) — filtre uygula
 *   onClear()     — filtre temizle
 */
export function ColumnFilterPopover({ column, filter, filterType, options, fetchOptions, resolveLabel, onApply, onClear }) {
  const [open, setOpen] = useState(false);
  const initialOp = filter?.op || DEFAULT_OP[filterType];
  const [op, setOp] = useState(initialOp);
  const [value, setValue] = useState(filter?.value ?? '');
  const [value2, setValue2] = useState(filter?.value2 ?? '');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  // asyncSelect (sunucu-arama) state
  const [asyncQuery, setAsyncQuery] = useState('');
  const [asyncResults, setAsyncResults] = useState([]);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const asyncReqRef = useRef(0);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setOp(filter?.op || DEFAULT_OP[filterType]);
    setValue(filter?.value ?? '');
    setValue2(filter?.value2 ?? '');
  }, [filter, filterType]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 240;
    const left = Math.min(Math.max(8, rect.right - popoverWidth), window.innerWidth - popoverWidth - 8);
    setPos({ top: rect.bottom + 4, left });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setAsyncQuery('');
    setAsyncResults([]);
    setTimeout(() => inputRef.current?.focus(), 0);
    const handleMouse = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const handleScroll = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleMouse);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleMouse);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  // asyncSelect: debounce'lu sunucu araması
  useEffect(() => {
    if (!open || filterType !== 'asyncSelect' || !fetchOptions) return undefined;
    const q = asyncQuery.trim();
    if (q.length < 1) { setAsyncResults([]); setAsyncLoading(false); return undefined; }
    setAsyncLoading(true);
    const reqId = asyncReqRef.current + 1;
    asyncReqRef.current = reqId;
    const t = setTimeout(async () => {
      try {
        const opts = await fetchOptions(q);
        if (asyncReqRef.current === reqId) setAsyncResults(opts || []);
      } catch {
        if (asyncReqRef.current === reqId) setAsyncResults([]);
      } finally {
        if (asyncReqRef.current === reqId) setAsyncLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [asyncQuery, open, filterType, fetchOptions]);

  const isActive = !!filter;
  const headerLabel = typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id;
  const operators = FILTER_OPERATORS[filterType] || FILTER_OPERATORS.text;
  const currentOp = getOperator(filterType, op);
  const inputType = filterType === 'number' ? 'number' : filterType === 'date' ? 'date' : 'text';

  const apply = () => {
    if (!currentOp) return;
    if (currentOp.needsValue && (value === '' || value === null || value === undefined)) return;
    if (currentOp.needsValue2 && (value2 === '' || value2 === null || value2 === undefined)) return;
    const next = { op: currentOp.id };
    if (currentOp.needsValue) next.value = value;
    if (currentOp.needsValue2) next.value2 = value2;
    onApply(next);
    setOpen(false);
  };
  const clear = () => {
    setValue(''); setValue2(''); setOp(DEFAULT_OP[filterType]);
    onClear(); setOpen(false);
  };

  return (
    <span className="c360-filter-wrap">
      <button
        ref={triggerRef}
        type="button"
        className={`c360-filter-trigger${isActive ? ' is-active' : ''}`}
        title={isActive ? `Filtreli: ${getOperator(filterType, filter.op)?.label || filter.op}` : 'Filtrele'}
        aria-label={`${headerLabel} filtre`}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      >
        <FilterFunnelIcon filled={isActive} />
      </button>
      {open && createPortal(
        <div
          ref={popoverRef}
          className="c360-filter-popover c360-filter-popover--portal"
          role="dialog"
          aria-label={`${headerLabel} filtresi`}
          style={{ top: pos.top, left: pos.left }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="c360-filter-label">{headerLabel}</div>
          {filterType !== 'select' && filterType !== 'asyncSelect' && (
            <select className="c360-filter-select" value={op} onChange={(e) => setOp(e.target.value)} style={{ marginBottom: 8 }}>
              {operators.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          )}
          {filterType === 'asyncSelect' ? (
            <>
              {isActive && (
                <div className="ui-ss-active-filter" style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent, #0073ea)', marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Seçili: {resolveLabel?.(filter.value) || 'kayıt'}
                </div>
              )}
              <input
                ref={inputRef}
                type="text"
                className="c360-filter-input"
                placeholder="Ara…"
                value={asyncQuery}
                onChange={(e) => setAsyncQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
              />
              <div className="ui-ss-list" style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6 }}>
                <div
                  className="ui-ss-opt ui-ss-opt--empty"
                  onClick={() => { onClear(); setOpen(false); }}
                >
                  Tümü
                </div>
                {asyncLoading && <div className="ui-ss-msg">Aranıyor…</div>}
                {!asyncLoading && asyncQuery.trim().length >= 1 && asyncResults.length === 0 && (
                  <div className="ui-ss-msg">Sonuç yok</div>
                )}
                {!asyncLoading && asyncQuery.trim().length < 1 && (
                  <div className="ui-ss-msg">Aramak için yazın…</div>
                )}
                {asyncResults.map((opt) => (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={opt.value === filter?.value}
                    className={`ui-ss-opt${opt.value === filter?.value ? ' is-selected' : ''}`}
                    onClick={() => { onApply({ op: 'equals', value: opt.value }); setOpen(false); }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            </>
          ) : filterType === 'select' ? (
            <select ref={inputRef} className="c360-filter-select" value={value} onChange={(e) => setValue(e.target.value)}>
              <option value="">Tümü</option>
              {options?.map((opt) => <option key={String(opt.value)} value={opt.value}>{opt.label}</option>)}
            </select>
          ) : (
            <>
              {currentOp.needsValue && (
                <input ref={inputRef} type={inputType} className="c360-filter-input"
                  placeholder={currentOp.needsValue2 ? 'Min' : currentOp.label}
                  value={value} onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !currentOp.needsValue2) apply(); if (e.key === 'Escape') setOpen(false); }} />
              )}
              {currentOp.needsValue2 && (
                <input type={inputType} className="c360-filter-input"
                  placeholder="Max" value={value2} onChange={(e) => setValue2(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') apply(); if (e.key === 'Escape') setOpen(false); }} style={{ marginTop: 6 }} />
              )}
            </>
          )}
          {filterType !== 'asyncSelect' && (
            <div className="c360-filter-actions">
              <button type="button" className="c360-filter-btn c360-filter-btn--clear" onClick={clear}>Temizle</button>
              <button type="button" className="c360-filter-btn c360-filter-btn--apply" onClick={apply}>Uygula</button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </span>
  );
}
